import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, CheckCircle, XCircle, Ban, Search, Filter, RefreshCw, Gamepad2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { fetchPendingUsers, fetchAllUsers, approveUser, rejectUser, banUser } from '../lib/api';
import ConfirmModal from '../components/ConfirmModal';

const Admin = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null
  });

  const showModal = (config) => {
    setModalConfig({
      isOpen: true,
      type: config.type || 'info',
      title: config.title || '',
      message: config.message || '',
      onConfirm: config.onConfirm || null
    });
  };

  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    loadUsers();
  }, [activeTab]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      let data;
      
      if (activeTab === 'pending') {
        data = await fetchPendingUsers();
      } else {
        data = await fetchAllUsers();
      }
      
      setUsers(data);
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setError('Não foi possível carregar os usuários. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      setActionLoading(true);
      await approveUser(userId);
      // Remover o usuário da lista ou atualizar seu status
      setUsers(users.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Erro ao aprovar usuário:', error);
      showModal({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao aprovar usuário. Tente novamente.'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (userId) => {
    try {
      setActionLoading(true);
      await rejectUser(userId);
      // Remover o usuário da lista ou atualizar seu status
      setUsers(users.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Erro ao rejeitar usuário:', error);
      showModal({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao rejeitar usuário. Tente novamente.'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleBan = async (userId) => {
    showModal({
      type: 'confirm',
      title: 'Confirmar banimento',
      message: 'Tem certeza que deseja banir este usuário? Esta ação é irreversível.',
      onConfirm: async () => {
        try {
          setActionLoading(true);
          await banUser(userId);
          setUsers(users.filter(u => u.id !== userId));
        } catch (error) {
          console.error('Erro ao banir usuário:', error);
          showModal({
            type: 'error',
            title: 'Erro',
            message: 'Erro ao banir usuário. Tente novamente.'
          });
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  // Filtrar usuários com base na busca
  const filteredUsers = users.filter(user => 
    user.discord_username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950 text-white p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard" className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors">
            <ArrowLeft size={18} />
            <span>Voltar ao Dashboard</span>
          </Link>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 to-fuchsia-600 rounded-full blur opacity-75"></div>
              <Users className="relative w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-black">Administração</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex mb-6 bg-black/30 backdrop-blur-md rounded-sm overflow-hidden">
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex-1 py-4 font-bold text-sm uppercase tracking-wider transition-all ${
              activeTab === 'pending'
                ? 'bg-gradient-to-r from-cyan-500 to-fuchsia-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Pendentes
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-1 py-4 font-bold text-sm uppercase tracking-wider transition-all ${
              activeTab === 'all'
                ? 'bg-gradient-to-r from-cyan-500 to-fuchsia-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Todos Usuários
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
            <input
              type="text"
              placeholder="Buscar por username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-sm pl-10 pr-4 py-3 text-sm outline-none focus:border-cyan-500/50 transition-colors"
            />
          </div>
          
          <button
            onClick={loadUsers}
            disabled={loading}
            className="px-4 py-3 bg-white/10 hover:bg-white/20 rounded-sm flex items-center gap-2 transition-all disabled:opacity-50"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            <span>Atualizar</span>
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-sm p-4 text-center">
            <p>{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredUsers.length === 0 && (
          <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-sm p-12 text-center">
            <Users size={48} className="mx-auto mb-4 text-gray-500" />
            <h3 className="text-xl font-bold mb-2">Nenhum usuário encontrado</h3>
            <p className="text-gray-400">
              {activeTab === 'pending' 
                ? 'Não há usuários pendentes de aprovação no momento.' 
                : 'Nenhum usuário corresponde aos critérios de busca.'}
            </p>
          </div>
        )}

        {/* Users Table */}
        {!loading && !error && filteredUsers.length > 0 && (
          <div className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5">
                  <th className="py-4 px-6 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Usuário</th>
                  <th className="py-4 px-6 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Discord ID</th>
                  <th className="py-4 px-6 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Status</th>
                  <th className="py-4 px-6 text-right text-xs font-bold uppercase tracking-wider text-gray-400">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-white/5 transition-colors">
                    <td className="py-4 px-6 flex items-center gap-3">
                      {user.discord_avatar ? (
                        <img 
                          src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.discord_avatar}.png`} 
                          alt={user.discord_username}
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                          <Gamepad2 size={16} className="text-gray-400" />
                        </div>
                      )}
                      <span>{user.discord_username}</span>
                    </td>
                    <td className="py-4 px-6 font-mono text-sm text-gray-400">{user.discord_id}</td>
                    <td className="py-4 px-6">
                      <span className={`px-2 py-1 text-xs rounded-sm ${
                        user.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                        user.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        user.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {user.status === 'approved' ? 'Aprovado' :
                         user.status === 'pending' ? 'Pendente' :
                         user.status === 'rejected' ? 'Rejeitado' :
                         user.status === 'banned' ? 'Banido' : 'Desconhecido'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {user.status !== 'approved' && (
                          <button
                            onClick={() => handleApprove(user.id)}
                            disabled={actionLoading}
                            className="p-2 bg-green-500/20 text-green-400 rounded-sm hover:bg-green-500/30 transition-colors disabled:opacity-50"
                            title="Aprovar"
                          >
                            <CheckCircle size={18} />
                          </button>
                        )}
                        {user.status !== 'rejected' && user.status !== 'banned' && (
                          <button
                            onClick={() => handleReject(user.id)}
                            disabled={actionLoading}
                            className="p-2 bg-yellow-500/20 text-yellow-400 rounded-sm hover:bg-yellow-500/30 transition-colors disabled:opacity-50"
                            title="Rejeitar"
                          >
                            <XCircle size={18} />
                          </button>
                        )}
                        {user.status !== 'banned' && (
                          <button
                            onClick={() => handleBan(user.id)}
                            disabled={actionLoading}
                            className="p-2 bg-red-500/20 text-red-400 rounded-sm hover:bg-red-500/30 transition-colors disabled:opacity-50"
                            title="Banir"
                          >
                            <Ban size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de confirmação/erro */}
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
      />
    </div>
  );
};

export default Admin;